<template>
  <ClAnimatePresence>
    <ClMotion
      v-if="open"
      key="nav-cards-overlay"
      preset="fade"
      as="div"
      class="nav-cards"
      role="dialog"
      aria-modal="true"
      aria-label="Apps"
      @keydown.esc="close"
    >
      <div class="nav-cards__inner">
        <div class="nav-cards__toolbar">
          <p class="nav-cards__hint">Open a workspace app. Tools are listed by daily use. The sidebar stays collapsed in this style.</p>
          <button type="button" class="nav-cards__close" @click="close" aria-label="Close apps">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <div class="nav-cards__grid">
          <button
            v-for="card in cards"
            :key="card.path"
            type="button"
            class="nav-card"
            :class="{ 'is-active': isActive(card) }"
            @click="openCard(card)"
          >
            <span
              v-if="navBadge(card.path)"
              class="nav-card__badge"
              :title="navBadgeLabel(card.path)"
            >{{ navBadge(card.path) }}</span>
            <span
              class="nav-card__icon"
              :style="{
                background: card.toneColors.bg,
                color: card.toneColors.fg,
              }"
              aria-hidden="true"
            >
              <i :class="card.icon"></i>
            </span>
            <span class="nav-card__title">{{ card.label }}</span>
            <span class="nav-card__blurb">{{ card.blurb }}</span>
            <span class="nav-card__open">Open →</span>
          </button>
        </div>
      </div>
    </ClMotion>
  </ClAnimatePresence>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/utils/api'
import eventBus from '@/utils/eventBus'
import ClAnimatePresence from '@/components/ds/ClAnimatePresence.vue'
import ClMotion from '@/components/ds/ClMotion.vue'
import { getSidebarNavCards } from '@/theme/sidebarNavCards'
import {
  INBOX_ACTIVITY_EVENT,
  INBOX_BADGE_POLL_MS,
  INBOX_PATH,
  readInboxLastSeen,
} from '@/utils/inboxLive'

const ALERTS_PATH = '/manage/alerts'
const WALLET_PATH = '/manage/store-wallet'

export default {
  name: 'SidebarNavCards',
  components: { ClAnimatePresence, ClMotion },
  data() {
    return {
      open: false,
      inboxUnreadCount: 0,
      inboxPollTimer: null,
      alertsOpenCount: 0,
      walletOpenCount: 0,
    }
  },
  computed: {
    ...mapGetters(['currentUser']),
    cards() {
      return getSidebarNavCards(this.currentUser)
    },
  },
  watch: {
    '$route.path'() {
      this.close()
    },
  },
  mounted() {
    eventBus.on('toggle-nav-cards', this.toggle)
    eventBus.on('open-nav-cards', this.show)
    eventBus.on('close-nav-cards', this.close)
    eventBus.on(INBOX_ACTIVITY_EVENT, this.onInboxActivity)
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    eventBus.off('toggle-nav-cards', this.toggle)
    eventBus.off('open-nav-cards', this.show)
    eventBus.off('close-nav-cards', this.close)
    eventBus.off(INBOX_ACTIVITY_EVENT, this.onInboxActivity)
    this.stopInboxBadgePoll()
    window.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('nav-cards-open')
    eventBus.emit('nav-cards-open-changed', false)
  },
  methods: {
    show() {
      this.open = true
      document.body.classList.add('nav-cards-open')
      eventBus.emit('nav-cards-open-changed', true)
      this.loadBadges()
      this.startInboxBadgePoll()
    },
    close() {
      if (!this.open) return
      this.open = false
      this.stopInboxBadgePoll()
      document.body.classList.remove('nav-cards-open')
      eventBus.emit('nav-cards-open-changed', false)
    },
    toggle() {
      if (this.open) this.close()
      else this.show()
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.open) this.close()
    },
    isActive(card) {
      const path = this.$route?.path || ''
      const tab = String(this.$route?.query?.tab || '')
      if (String(card.path || '').includes('tab=broadcasts')) {
        return (
          (path === '/manage/templates' && (tab === 'broadcasts' || tab === 'campaigns'))
          || path.startsWith('/manage/templates/broadcasts')
        )
      }
      if (card.path === '/manage/templates') {
        if (tab === 'broadcasts' || tab === 'campaigns') return false
        if (path.startsWith('/manage/templates/broadcasts')) return false
        return path === card.path || path.startsWith(`${card.path}/`)
      }
      if (card.exact || card.path === '/dashboard') return path === card.path
      return path === card.path || path.startsWith(`${card.path}/`)
    },
    openCard(card) {
      if (!card?.path) return
      this.close()
      this.$router.push(card.path).catch(() => {})
    },
    navBadgeCount(path) {
      if (path === ALERTS_PATH) return this.alertsOpenCount
      if (path === INBOX_PATH) return this.inboxUnreadCount
      if (path === WALLET_PATH) return this.walletOpenCount
      return 0
    },
    navBadge(path) {
      const count = this.navBadgeCount(path)
      if (!count) return ''
      return count > 99 ? '99+' : String(count)
    },
    navBadgeLabel(path) {
      const count = this.navBadgeCount(path)
      if (path === INBOX_PATH) {
        return `${count} ${count === 1 ? 'chat' : 'chats'} with new customer messages`
      }
      if (path === WALLET_PATH) {
        return `${count} open payout ${count === 1 ? 'request' : 'requests'}`
      }
      return `${count} new stock management issues`
    },
    async loadBadges() {
      const paths = this.cards.map((card) => card.path)
      const jobs = []
      if (paths.includes(INBOX_PATH)) {
        const since = readInboxLastSeen(this.currentUser)
        jobs.push(
          api.get('/admin/inbox/unread-count/', { params: since ? { since } : {} }).then(({ data }) => {
            this.inboxUnreadCount = Number(data?.count) || 0
          }).catch(() => {
            this.inboxUnreadCount = 0
          }),
        )
      }
      if (paths.includes(ALERTS_PATH)) {
        jobs.push(
          api.get('/businesses/alerts/summary/').then(({ data }) => {
            this.alertsOpenCount = Number(data?.open) || 0
          }).catch(() => {
            this.alertsOpenCount = 0
          }),
        )
      }
      if (paths.includes(WALLET_PATH)) {
        jobs.push(
          api.get('/wallets/store-wallets/').then(({ data }) => {
            this.walletOpenCount = Number(data?.totals?.open_withdrawals_count) || 0
          }).catch(() => {
            this.walletOpenCount = 0
          }),
        )
      }
      await Promise.all(jobs)
    },
    onInboxActivity() {
      if (this.open) this.loadBadges()
    },
    startInboxBadgePoll() {
      this.stopInboxBadgePoll()
      this.inboxPollTimer = setInterval(() => {
        if (this.open) this.loadBadges()
      }, INBOX_BADGE_POLL_MS)
    },
    stopInboxBadgePoll() {
      if (this.inboxPollTimer) clearInterval(this.inboxPollTimer)
      this.inboxPollTimer = null
    },
  },
}
</script>

<style scoped>
.nav-cards {
  position: fixed;
  top: var(--navbar-height, 64px);
  left: var(--sidebar-collapsed-width, 80px);
  right: 0;
  bottom: 0;
  z-index: 900;
  overflow: auto;
  background: var(--clinch-page-bg, #eef3f8);
  color: var(--clinch-text-heading, var(--clinch-navy, #001f3f));
}

.nav-cards__inner {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 1.15rem 1.15rem 2.25rem;
}

.nav-cards__toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}

.nav-cards__hint {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #64748b);
}

.nav-cards__close {
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  background: #fff;
  color: var(--clinch-navy, #001f3f);
  cursor: pointer;
}

.nav-cards__close:hover {
  background: #f8fafc;
}

.nav-cards__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.15rem;
}

.nav-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 220px;
  padding: 1.4rem 1.45rem 1.2rem;
  border: 1.5px solid var(--clinch-primary, #33b5ff);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.nav-card:hover,
.nav-card.is-active {
  transform: translateY(-2px);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
}

.nav-card__badge {
  position: absolute;
  top: 0.85rem;
  right: 0.9rem;
  min-width: 1.35rem;
  padding: 0.12rem 0.4rem;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.nav-card__icon {
  width: 3.35rem;
  height: 3.35rem;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.45rem;
  margin-bottom: 1rem;
}

.nav-card__title {
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--clinch-text-heading, var(--clinch-navy, #001f3f));
}

.nav-card__blurb {
  margin-top: 0.35rem;
  font-size: 0.92rem;
  line-height: 1.4;
  color: var(--text-secondary, #64748b);
}

.nav-card__open {
  margin-top: auto;
  padding-top: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--clinch-blue, #2563eb);
}

@media (max-width: 1100px) {
  .nav-cards__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .nav-cards {
    left: 0;
  }
}

@media (max-width: 640px) {
  .nav-cards__inner {
    padding: 0.85rem 0.85rem 1.75rem;
  }

  .nav-cards__grid {
    grid-template-columns: 1fr;
  }
}
</style>
